import type { FC } from 'react';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import {
  Box,
  Column,
  Columns,
  IconClear,
  IconRecommended,
  Text,
  TextLinkButton,
} from 'braid-design-system';

interface CustomBillableAlertProps {
  openBillableDialog: () => void;
  hideAlert: () => void;
}

/**
 * CustomBillableAlert
 * @description This component is a custom version of Alert component in Braid:
 * https://github.com/seek-oss/braid-design-system/blob/561d50f28f85a3828cfa3b4356226003ff179eec/packages/braid-design-system/src/lib/components/Alert/Alert.tsx
 *
 * This is required because Braid team does not recommend using Alert component with a custom icon
 * https://seekchat.slack.com/archives/CDL5VKRHN/p1699567172702949
 *
 * We ran user interviews with the Recommended Icon and got positive feedback from users. So, for the CPQA experiment 2, we will test with this approach
 */
export const CustomBillableAlert: FC<CustomBillableAlertProps> = ({
  openBillableDialog,
  hideAlert,
}) => {
  const { t } = useTranslations(translations);

  const tone = 'promote';

  return (
    <Box
      background="promoteLight"
      paddingY="medium"
      paddingX="gutter"
      borderRadius="large"
      position="relative"
      overflow="hidden"
      role="alert"
      aria-live="polite"
    >
      <Columns space="small">
        <Column width="content">
          <IconRecommended tone={tone} />
        </Column>
        <Column>
          <Box paddingY="xxsmall">
            <Text>
              {t(
                "For Pay Per Application ads, you'll be billed for applications marked with a Billable badge. More about <TextLinkButton>which applications are ‘billable’</TextLinkButton>.",
                {
                  TextLinkButton: (children) => (
                    <TextLinkButton onClick={openBillableDialog}>
                      {children}
                    </TextLinkButton>
                  ),
                },
              )}
            </Text>
          </Box>
        </Column>

        <Column width="content">
          <Box
            component="button"
            aria-label={t('Close')}
            borderRadius="full"
            cursor="pointer"
            position="relative"
            onClick={() => hideAlert()}
            outline="none"
            transition="touchable"
            transform={{ active: 'touchable' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              component="span"
              display="block"
              zIndex={1}
              position="relative"
            >
              <IconClear size="fill" tone={tone} />
            </Box>
          </Box>
        </Column>
      </Columns>
    </Box>
  );
};
